import React, { useCallback, useEffect, useState } from 'react';
import { federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import { track, REVIEW_COMPLETED } from '../../utils/analytics';
import FederationGate from './FederationGate';
import {
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  REVIEW_OUTCOMES,
  btnPrimary,
  canWrite,
  inputClass,
  isViewer,
  safeText,
} from './federationShared';

function ReviewsInner() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const viewer = isViewer(user);

  const [reviews, setReviews] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    sourceFindingId: '',
    sourceActionId: '',
    reviewReportId: '',
    reviewMatchId: '',
    reviewOutcome: 'Not reviewed',
    reviewNotes: '',
  });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    Promise.all([
      federation.getReviews(),
      federation.getFindings().catch(() => ({ findings: [] })),
    ])
      .then(([r, f]) => {
        setReviews(r.reviews || []);
        setFindings(f.findings || []);
      })
      .catch((e) => {
        setReviews([]);
        setError(e.message || 'Could not load reviews');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!writable) return;
    if (!form.sourceFindingId && !form.sourceActionId) {
      setError('A source finding or source action is required');
      return;
    }
    if (!form.reviewReportId && !form.reviewMatchId && !form.reviewNotes.trim()) {
      setError('Review notes are required when no optional integration evidence is linked');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await federation.createReview({
        sourceFindingId: form.sourceFindingId || undefined,
        sourceActionId: form.sourceActionId || undefined,
        reviewReportId: form.reviewReportId || undefined,
        reviewMatchId: form.reviewMatchId || undefined,
        reviewOutcome: form.reviewOutcome,
        reviewNotes: form.reviewNotes.trim() || undefined,
      });
      track(REVIEW_COMPLETED, { reviewOutcome: form.reviewOutcome });
      setForm({
        sourceFindingId: '',
        sourceActionId: '',
        reviewReportId: '',
        reviewMatchId: '',
        reviewOutcome: 'Not reviewed',
        reviewNotes: '',
      });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message || 'Could not create review');
    } finally {
      setSaving(false);
    }
  };

  const handleOutcomeChange = async (review, reviewOutcome) => {
    if (!writable) return;
    setError('');
    try {
      await federation.updateReview({ id: review.id, reviewOutcome });
      load();
    } catch (err) {
      setError(err.message || 'Could not update review');
    }
  };

  return (
    <>
      <PageHeader
        title="Reviews"
        description="Leadership reviews of organizational findings and assigned actions."
        actions={
          writable && !viewer ? (
            <button type="button" className={btnPrimary} onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Cancel' : 'Create review'}
            </button>
          ) : null
        }
      />

      <ErrorBanner message={error} onRetry={load} />

      {showForm && writable && (
        <form onSubmit={handleCreate} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          <h2 className="text-base font-semibold text-navy-900">New review</h2>
          <p className="text-xs text-gray-500">
            Review a finding or action directly. Match/report IDs are optional integration evidence.
          </p>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Source finding</label>
            <select
              className={inputClass}
              value={form.sourceFindingId}
              onChange={(e) => setForm((f) => ({ ...f, sourceFindingId: e.target.value }))}
            >
              <option value="">Optional if action provided</option>
              {findings.map((f) => (
                <option key={f.id} value={f.id}>
                  {safeText(f.findingText).slice(0, 80)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Source action ID</label>
            <input
              className={inputClass}
              value={form.sourceActionId}
              onChange={(e) => setForm((f) => ({ ...f, sourceActionId: e.target.value }))}
              placeholder="Optional action ID"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Optional report ID</label>
              <input
                className={inputClass}
                value={form.reviewReportId}
                onChange={(e) => setForm((f) => ({ ...f, reviewReportId: e.target.value }))}
                placeholder="Optional integration source"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Optional match ID</label>
              <input
                className={inputClass}
                value={form.reviewMatchId}
                onChange={(e) => setForm((f) => ({ ...f, reviewMatchId: e.target.value }))}
                placeholder="Optional integration source"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Outcome</label>
            <select
              className={inputClass}
              value={form.reviewOutcome}
              onChange={(e) => setForm((f) => ({ ...f, reviewOutcome: e.target.value }))}
            >
              {REVIEW_OUTCOMES.filter((o) => o !== 'Resolved').map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Resolved can be set after create via update.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Notes</label>
            <textarea
              className={inputClass}
              rows={3}
              value={form.reviewNotes}
              onChange={(e) => setForm((f) => ({ ...f, reviewNotes: e.target.value }))}
              maxLength={4000}
            />
          </div>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Creating…' : 'Create review'}
          </button>
        </form>
      )}

      {loading && <LoadingBlock label="Loading reviews…" />}

      {!loading && reviews.length === 0 && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No reviews yet</p>
          <p className="text-sm text-gray-500 mt-2">
            Create a review when later-match evidence is available for a finding or action.
          </p>
        </div>
      )}

      {!loading && reviews.length > 0 && (
        <ul className="space-y-3">
          {reviews.map((review) => (
            <li key={review.id} className="border border-navy-200 rounded-xl bg-white px-4 py-4">
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-2">
                <span>Outcome: {safeText(review.reviewOutcome)}</span>
                <span>Status: {safeText(review.reviewStatus)}</span>
                <span>{safeText(review.createdAt)}</span>
              </div>
              {review.reviewNotes ? (
                <p className="text-sm text-navy-900 whitespace-pre-wrap">{safeText(review.reviewNotes)}</p>
              ) : (
                <p className="text-sm text-gray-500">No notes</p>
              )}
              <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-gray-500">
                {review.sourceFindingId ? <span>Finding: {safeText(review.sourceFindingId)}</span> : null}
                {review.sourceActionId ? <span>Action: {safeText(review.sourceActionId)}</span> : null}
                {review.reviewReportId ? <span>Report: {safeText(review.reviewReportId)}</span> : null}
                {review.reviewMatchId ? <span>Match: {safeText(review.reviewMatchId)}</span> : null}
              </div>
              {writable && (
                <div className="mt-3 max-w-xs">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Update outcome</label>
                  <select
                    className={inputClass}
                    value={review.reviewOutcome || 'Not reviewed'}
                    onChange={(e) => handleOutcomeChange(review, e.target.value)}
                  >
                    {REVIEW_OUTCOMES.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function FederationReviews() {
  return (
    <FederationGate>
      <ReviewsInner />
    </FederationGate>
  );
}
